/**
 * MKS-410 — the reporter's page: one "New request" form, plus "My requests".
 *
 * WHAT CHANGED. Until MKS-410 this was a DIRECTORY page (quick task
 * 260727-gob): it posted nothing, and listed the open project boards for the
 * user to pick from. Picking a board is exactly what the ticket removes — the
 * reporter is not asked to know which team owns their problem — so the page is
 * now a form that files a request with no project and no assignee, and a list
 * of what became of the ones already filed.
 *
 * RELEASE FLAG. Behind `mks_339_helpdesk_inbox`, the EXISTING helpdesk flag,
 * not a new one: MKS-410 extends the same dark feature. The flag-OFF branch is
 * `ReportIssueDirectory` below — today's page, moved down this file unchanged —
 * so production keeps exactly today's behaviour until the ticket is verified.
 *
 * `RequireFlag` is deliberately NOT used on the route, for the reason
 * `HelpdeskPage` states: it renders a 404, and a 404 on a page that is in the
 * menu is worse than the page it replaced. The page reads the flag itself.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getOpenBoards } from '../services/api/projects';
import { useMyReports } from '../hooks/useMyReports';
import { useFlag } from '../services/flagsContext';
import { useMyHelpdeskRequests } from '../hooks/useHelpdeskRequests';
import { Spinner } from '../components/common/Spinner';
import { formatDisplayDateTime } from '../utils/format';
import { FolderIcon, ClipboardDocumentListIcon } from '../components/ui/icons';
import { NewHelpdeskRequestForm } from '../components/helpdesk/NewHelpdeskRequestForm';
import { MyRequestsList } from '../components/helpdesk/MyRequestsList';

export function ReportIssuePage() {
  const helpdeskOn = useFlag('mks_339_helpdesk_inbox');
  // Disabled while the flag is off: the backend route answers 404 there, and
  // an error-state query behind a page that never renders it is noise in
  // Sentry for no gain.
  const myRequests = useMyHelpdeskRequests(helpdeskOn);

  if (!helpdeskOn) return <ReportIssueDirectory />;

  return (
    <div className="p-4 max-w-3xl" data-testid="helpdesk-request-page">
      <h1 className="text-xl font-semibold mb-1">Helpdesk</h1>
      <p className="text-sm text-gray-500 mb-6">
        Tell us what you need. You don&apos;t have to know which team handles it — we
        route it for you.
      </p>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">New request</h2>
        <NewHelpdeskRequestForm />
      </section>

      <section>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
          <ClipboardDocumentListIcon className="w-4 h-4" />
          My requests
        </h2>
        <MyRequestsList
          requests={myRequests.data}
          isLoading={myRequests.isLoading}
          isError={myRequests.isError}
        />
      </section>
    </div>
  );
}

/**
 * The flag-OFF branch — quick task 260727-gob's Report-an-issue page,
 * unchanged, so production renders byte-for-byte what it renders today until
 * `mks_339_helpdesk_inbox` is switched on.
 *
 * Two sections:
 *   (a) "Open project boards" — every project with openToAllInternal=true
 *       (GET /api/projects/open-boards), each row linking to its Kanban
 *       board (`/projects/:id/board`) so the user can drop in a task or
 *       comment without being a project member.
 *   (b) "My reports" — tasks the caller has created across ALL projects
 *       (GET /api/tasks/my-reports), newest first, deep-linking to
 *       `/projects/:projectId/board?openTask=:id` (the same `?openTask=`
 *       contract used by MyTasksPage / SlaBreachDashboardPage —
 *       KanbanBoardPage reads `searchParams.get('openTask')`).
 */
function ReportIssueDirectory() {
  const openBoardsQuery = useQuery({
    queryKey: ['projects', 'open-boards'],
    queryFn: getOpenBoards,
    staleTime: 60_000,
  });
  const myReportsQuery = useMyReports();

  return (
    <div className="p-4 max-w-3xl">
      <h1 className="text-xl font-semibold mb-1">Report an issue</h1>
      <p className="text-sm text-gray-500 mb-6">
        Drop a bug or task on any open project board below — no project membership needed.
      </p>

      {/* Open project boards */}
      <section className="mb-8">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
          <FolderIcon className="w-4 h-4" />
          Open project boards
        </h2>
        {openBoardsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : openBoardsQuery.isError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
            Failed to load open project boards.
          </div>
        ) : (openBoardsQuery.data ?? []).length === 0 ? (
          <div className="text-sm text-gray-500">
            No project boards are open to all employees yet.
          </div>
        ) : (
          <ul className="bg-white border border-gray-200 rounded divide-y divide-gray-100">
            {(openBoardsQuery.data ?? []).map((p) => (
              <li key={p.id}>
                <Link
                  to={`/projects/${p.id}/board`}
                  className="block px-3 py-2 text-sm text-primary-700 hover:bg-gray-50"
                >
                  {p.name ?? `Project #${p.id}`}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* My reports */}
      <section>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
          <ClipboardDocumentListIcon className="w-4 h-4" />
          My reports
        </h2>
        {myReportsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : myReportsQuery.isError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
            Failed to load your reports.
          </div>
        ) : (myReportsQuery.data ?? []).length === 0 ? (
          <div className="text-sm text-gray-500">You haven&apos;t reported anything yet.</div>
        ) : (
          <ul className="bg-white border border-gray-200 rounded divide-y divide-gray-100">
            {(myReportsQuery.data ?? []).map((r) => (
              <li key={r.id}>
                <Link
                  to={`/projects/${r.projectId}/board?openTask=${r.id}`}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                >
                  <span className="truncate">
                    {r.code ? <span className="text-gray-400 mr-1 text-xs">{r.code}</span> : null}
                    {r.name}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-gray-500 shrink-0">
                    <span>{r.projectName}</span>
                    <span>·</span>
                    <span>{r.stateName}</span>
                    <span>·</span>
                    <span>{formatDisplayDateTime(r.createdAt)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
